import { useCallback, useRef, useState } from "react";

// Simple incrementing id — fine for a single-tab UI notification stack.
let idCounter = 0;

/**
 * useToast — lightweight, self-contained toast/notification manager.
 *
 * Deliberately NOT a React Context / app-wide provider: each page that
 * needs toasts calls this hook itself and renders <ToastContainer /> in
 * its own tree. That keeps the whole system opt-in per page (no changes
 * required to App.jsx or any other route) while still being fully
 * reusable — any other page can adopt the exact same hook + container.
 *
 * Usage:
 *   const { toasts, toast } = useToast();
 *   toast.success("Saved!");
 *   toast.error("Something went wrong.");
 *   <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />
 */
export function useToast() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback(
    (message, variant = "info", duration = 4200) => {
      const id = ++idCounter;
      setToasts((prev) => [...prev, { id, message, variant }]);
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const toast = {
    success: (message, duration) => push(message, "success", duration),
    error: (message, duration) => push(message, "error", duration ?? 5500),
    warning: (message, duration) => push(message, "warning", duration),
    info: (message, duration) => push(message, "info", duration),
    dismiss,
  };

  return { toasts, toast };
}

export default useToast;